import { Button } from "@/components/ui/button";
import { Linkedin, Mail, ArrowRight } from "lucide-react";
import { SITE } from "@/config/site";
import { useNavigate } from "react-router-dom";

export default function FounderSection() {
  const navigate = useNavigate();
  const f = SITE.founder;

  return (
    <section
      id="founder"
      className="py-20 sm:py-24 bg-white border-y border-slate-100"
      data-testid="founder-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Photo */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative">
              <div
                className="absolute -inset-3 rounded-full bg-gradient-to-br from-primary/20 via-amber-100 to-transparent blur-2xl"
                aria-hidden="true"
              />
              <div
                className="relative h-72 w-72 sm:h-96 sm:w-96 rounded-full overflow-hidden ring-4 ring-white shadow-2xl bg-gradient-to-br from-primary/15 to-amber-100"
                data-testid="founder-photo"
              >
                {f.photoUrl ? (
                  <img
                    src={f.photoUrl}
                    alt="Marie Thelusma-Chase, CFRE, Grant Writing Consultant and Founder of Present You Perfect"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "50% 30%" }}
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <span className="font-heading text-6xl sm:text-7xl font-semibold text-primary/70 tracking-tight">
                      {f.initials}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
              {f.sectionEyebrow}
            </span>
            <h2
              className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              data-testid="founder-name"
            >
              {f.sectionHeadline}
            </h2>
            <p className="mt-1 text-slate-500 text-sm">{f.title}</p>

            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              {f.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* Contact */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 text-sm">
              <a
                href={`mailto:${f.email}`}
                className="inline-flex items-center gap-2 text-slate-700 hover:text-primary transition-colors"
                data-testid="founder-email-link"
              >
                <Mail className="h-4 w-4" />
                {f.email}
              </a>
              <a
                href={f.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-slate-700 hover:text-primary transition-colors"
                data-testid="founder-linkedin-link"
              >
                <Linkedin className="h-4 w-4" />
                LinkedIn Profile
              </a>
            </div>

            {/* Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="rounded-full px-7 py-6 text-base bg-primary hover:bg-teal-900"
                onClick={() => navigate("/build")}
                data-testid="founder-cta-start"
              >
                {f.ctaPrimaryLabel}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <a
                href={f.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-cta-linkedin"
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 py-6 text-base border-slate-200 w-full sm:w-auto"
                >
                  <Linkedin className="h-4 w-4 mr-2" />
                  {f.ctaSecondaryLabel}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
